import React, { useState, useCallback } from 'react';
import { Box, Typography, Button, Tabs, Tab, Drawer, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import EnhancedMeasurementPanel from './EnhancedMeasurementPanel';
import CompactPocusReport from './CompactPocusReport';
import './DicomUnifiedViewer.css';

/**
 * Standalone drawer component that can be used with any viewer
 */
const ViewerDrawer = ({ 
    fileName, 
    onGridLayoutChange, 
    currentGridLayout, 
    onDrawerToggle,
    selectedFile,
    patientInfo,
    // Measurement functionality props
    onMeasurementSelection,
    onMeasurementComplete,
    activeMeasurementTool,
    isInMeasurementMode,
    onClearAllMeasurements,
    onToolActivation,
    // Playback control props
    fileInfo,
    isPlaying,
    currentFrame,
    totalFrames,
    frameRate,
    onTogglePlayPause,
    onFrameSliderChange,
    onFrameRateChange,
    onFrameNavigation,
    // Tool control props
    activeTool,
    onSelectTool,
    annotationMode,
    onToggleAnnotationMode,
    currentColor,
    onColorChange,
    currentLineWidth,
    onLineWidthChange,
    onClearAnnotations,
    onToggleAnnotationVisibility,
    onResetViewport,
    onInvertImage,
    // Window/level info
    windowWidth,
    windowCenter,
    // Active viewport info
    getActiveViewportFileInfo
}) => {
    const [rightDrawerOpen, setRightDrawerOpen] = useState(true);
    const [activeTab, setActiveTab] = useState(0); // 0: Tools, 1: Measure, 2: Report, 3: Notes
    const [studyNotes, setStudyNotes] = useState('');

    // Handle drawer tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setRightDrawerOpen(true);
    };

    // Toggle right drawer
    const toggleRightDrawer = () => {
        const newState = !rightDrawerOpen;
        setRightDrawerOpen(newState);
        if (onDrawerToggle) {
            onDrawerToggle(newState);
        }
    };

    return (
        <>
            {/* MUI Drawer with mini variant */}
            <Drawer
                variant="permanent"
                anchor="right"
                open={rightDrawerOpen}
                sx={{
                    width: rightDrawerOpen ? 500 : 60,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: rightDrawerOpen ? 500 : 60,
                        boxSizing: 'border-box',
                        backgroundColor: '#242424',
                        borderLeft: '1px solid #333',
                        transition: 'width 0.3s ease',
                        overflowX: 'hidden',
                        zIndex: 1000,
                    },
                }}
            >
                {/* Mini drawer content when collapsed */}
                {!rightDrawerOpen && (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        pt: 2, 
                        gap: 1 
                    }}>
                        <IconButton 
                            onClick={toggleRightDrawer}
                            sx={{ color: '#e0e0e0' }}
                            title="Expand drawer"
                        >
                            <ChevronLeft />
                        </IconButton>
                        <IconButton sx={{ color: '#e0e0e0' }} title="Tools">
                            🔧
                        </IconButton>
                        <IconButton sx={{ color: '#e0e0e0' }} title="Measure">
                            📏
                        </IconButton>
                        <IconButton sx={{ color: '#e0e0e0' }} title="Report">
                            📊
                        </IconButton>
                        <IconButton sx={{ color: '#e0e0e0' }} title="Notes">
                            📝
                        </IconButton>
                    </Box>
                )}

                {/* Full drawer content when expanded */}
                {rightDrawerOpen && (
                    <>
                        <div className="right-drawer-header">
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                className="right-tabs"
                            >
                                <Tab label="Tools" />
                                <Tab label="Measure" />
                                <Tab label="Report" />
                                <Tab label="Notes" />
                            </Tabs>
                            <IconButton
                                onClick={toggleRightDrawer}
                                sx={{ color: '#aaa' }}
                                title="Collapse drawer"
                            >
                                <ChevronRight />
                            </IconButton>
                        </div>

                        <div className="right-drawer-content">
                    {/* Tools Tab */}
                    {activeTab === 0 && (
                        <div className="tools-panel">
                            {/* Multiframe controls section - ONLY show for files with multiple frames or video */}
                            {(() => {
                                // Get the active viewport file info - ALWAYS use the function if available
                                const activeFileInfo = getActiveViewportFileInfo ? getActiveViewportFileInfo() : (fileInfo || selectedFile);
                                
                                // STRICT CHECK: Only show playback controls if:
                                // 1. SOP Class UID indicates multiframe (1.2.840.10008.5.1.4.1.1.3.1) OR
                                // 2. File has more than 1 frame (multiframe DICOM) OR
                                // 3. File is MP4/video
                                
                                // Check multiple possible locations for SOP Class UID
                                const sopClassUID = activeFileInfo?.sopClassUID || 
                                                  activeFileInfo?.ahiMetadata?.SOPClassUID || 
                                                  activeFileInfo?.ahiMetadata?.MediaStorageSOPClassUID;
                                                  
                                const isMultiframeBySOP = sopClassUID === '1.2.840.10008.5.1.4.1.1.3.1'; // Ultrasound Multiframe Image Storage
                                const hasMultipleFrames = activeFileInfo?.numberOfFrames > 1;
                                const isVideo = activeFileInfo?.isMP4 || fileInfo?.isMP4;
                                
                                // Additional check: if it's a single frame SOP, explicitly hide controls
                                const isSingleFrameBySOP = sopClassUID === '1.2.840.10008.5.1.4.1.1.6.1'; // Ultrasound Image Storage (single frame)
                                
                                // CRITICAL FIX: If it's a single frame image (numberOfFrames = 1 or undefined), don't treat as video
                                const isTrueVideo = isVideo && hasMultipleFrames;
                                
                                // Debug logging removed - functionality working correctly
                                
                                // EXPLICIT RULE: If it's a single frame SOP, NEVER show playback controls
                                if (isSingleFrameBySOP) {
                                    return false;
                                }
                                
                                // CRITICAL FIX: Only show playback controls for true multiframe content
                                // - Multiframe by SOP Class UID OR
                                // - Has multiple frames OR  
                                // - Is a true video (video format AND has multiple frames)
                                return isMultiframeBySOP || hasMultipleFrames || isTrueVideo;
                            })() && (
                                <div className="tool-section">
                                    <Typography variant="subtitle2" gutterBottom>
                                        Playback Controls
                                    </Typography>
                                    <div className="playback-controls-drawer">
                                        <div className="playback-buttons">
                                            <button
                                                onClick={() => {
                                                    if (onFrameNavigation) {
                                                        onFrameNavigation('previous');
                                                    }
                                                }}
                                                className="frame-nav-button"
                                                title="Previous frame"
                                                disabled={!onFrameNavigation}
                                            >
                                                ◀
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (onTogglePlayPause) {
                                                        onTogglePlayPause();
                                                    }
                                                }}
                                                className="play-button"
                                                disabled={!onTogglePlayPause}
                                            >
                                                {isPlaying ? '⏸' : '▶'}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (onFrameNavigation) {
                                                        onFrameNavigation('next');
                                                    }
                                                }}
                                                className="frame-nav-button"
                                                title="Next frame"
                                                disabled={!onFrameNavigation}
                                            >
                                                ▶
                                            </button>
                                        </div>

                                        {/* Only show frame slider when explicitly paused and has meaningful frame data */}
                                        {isPlaying === false && totalFrames > 1 && currentFrame >= 1 && (
                                            <div className="frame-control">
                                                <span>Frame: {currentFrame || 1}/{totalFrames || 40}</span>
                                                <input
                                                    type="range"
                                                    min="1"
                                                    max={totalFrames || 40}
                                                    value={currentFrame || 1}
                                                    onChange={onFrameSliderChange || (() => {})}
                                                    onInput={onFrameSliderChange || (() => {})}
                                                    className="frame-slider"
                                                    style={{ width: '100%' }}
                                                />
                                            </div>
                                        )}

                                        <div className="frame-rate-control">
                                            <span>FPS: {frameRate || 30}</span>
                                            <input
                                                type="range"
                                                min="5"
                                                max="60"
                                                value={frameRate || 30}
                                                onChange={onFrameRateChange || (() => {})}
                                                className="frame-rate-slider"
                                                style={{ width: '100%' }}
                                            />
                                        </div>

                                        <div className="keyboard-shortcuts-hint">
                                            <span title="Keyboard shortcuts">⌨️ Space: Play/Pause | ←→: Frames</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Annotation tools section - always visible */}
                            <div className="tool-section">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                    <Typography variant="subtitle2">
                                        Annotation Tools
                                    </Typography>
                                    {/* Annotation Mode Switch */}
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={annotationMode}
                                            onChange={onToggleAnnotationMode}
                                            style={{ display: 'none' }}
                                        />
                                        <div
                                            style={{
                                                width: '40px',
                                                height: '20px',
                                                backgroundColor: annotationMode ? '#4285f4' : '#666',
                                                borderRadius: '10px',
                                                position: 'relative',
                                                transition: 'background-color 0.3s',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    backgroundColor: 'white',
                                                    borderRadius: '50%',
                                                    position: 'absolute',
                                                    top: '2px',
                                                    left: annotationMode ? '22px' : '2px',
                                                    transition: 'left 0.3s',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                }}
                                            />
                                        </div>
                                    </label>
                                </div>
                                <div className="annotation-tools">
                                    <div className="annotation-tool-row">
                                        <button
                                            className={`tool-button ${annotationMode && activeTool === 'pencil' ? 'active' : ''}`}
                                            onClick={() => {
                                                if (annotationMode && onSelectTool) onSelectTool('pencil');
                                            }}
                                            title={annotationMode ? "Pencil Tool - Draw freehand" : "Enable annotation mode to use this tool"}
                                            disabled={!annotationMode}
                                            style={{
                                                opacity: annotationMode ? 1 : 0.5,
                                                cursor: annotationMode ? 'pointer' : 'not-allowed'
                                            }}
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            className={`tool-button ${annotationMode && activeTool === 'text' ? 'active' : ''}`}
                                            onClick={() => {
                                                if (annotationMode && onSelectTool) onSelectTool('text');
                                            }}
                                            title={annotationMode ? "Text Tool - Add text annotations" : "Enable annotation mode to use this tool"}
                                            disabled={!annotationMode}
                                            style={{
                                                opacity: annotationMode ? 1 : 0.5,
                                                cursor: annotationMode ? 'pointer' : 'not-allowed'
                                            }}
                                        >
                                            📝
                                        </button>
                                        <button
                                            className={`tool-button ${annotationMode && activeTool === 'arrow' ? 'active' : ''}`}
                                            onClick={() => {
                                                if (annotationMode && onSelectTool) onSelectTool('arrow');
                                            }}
                                            title={annotationMode ? "Arrow Tool - Draw arrows" : "Enable annotation mode to use this tool"}
                                            disabled={!annotationMode}
                                            style={{
                                                opacity: annotationMode ? 1 : 0.5,
                                                cursor: annotationMode ? 'pointer' : 'not-allowed'
                                            }}
                                        >
                                            ➡️
                                        </button>
                                            <button
                                                className="tool-button"
                                                onClick={annotationMode ? onClearAnnotations : undefined}
                                                title={annotationMode ? (fileInfo?.numberOfFrames > 1 ? "Clear Frame Annotations" : "Clear All Annotations") : "Enable annotation mode to use this tool"}
                                                disabled={!annotationMode}
                                                style={{
                                                    opacity: annotationMode ? 1 : 0.5,
                                                    cursor: annotationMode ? 'pointer' : 'not-allowed'
                                                }}
                                            >
                                                🗑️
                                            </button>
                                            <button
                                                className="tool-button"
                                                onClick={onToggleAnnotationVisibility}
                                                title="Toggle annotation visibility"
                                                style={{
                                                    opacity: 1,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                👁️
                                            </button>
                                    </div>
                                    <div className="annotation-controls-row">
                                        <input
                                            type="color"
                                            value={currentColor || '#FF0000'}
                                            onChange={annotationMode ? onColorChange : undefined}
                                            className="color-picker"
                                            title={annotationMode ? "Pick color" : "Enable annotation mode to use this tool"}
                                            disabled={!annotationMode}
                                            style={{
                                                opacity: annotationMode ? 1 : 0.5,
                                                cursor: annotationMode ? 'pointer' : 'not-allowed'
                                            }}
                                        />
                                        <select
                                            value={currentLineWidth || 2}
                                            onChange={annotationMode ? onLineWidthChange : undefined}
                                            className="line-width-select"
                                            title={annotationMode ? "Line width" : "Enable annotation mode to use this tool"}
                                            disabled={!annotationMode}
                                            style={{
                                                opacity: annotationMode ? 1 : 0.5,
                                                cursor: annotationMode ? 'pointer' : 'not-allowed'
                                            }}
                                        >
                                            <option value="1">Thin</option>
                                            <option value="2">Normal</option>
                                            <option value="4">Thick</option>
                                            <option value="6">Very Thick</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="tool-section">
                                <Typography variant="subtitle2" gutterBottom>
                                    View Controls
                                </Typography>
                                <div className="tool-buttons-grid">
                                    <button
                                        className={`tool-button ${activeTool === 'WindowLevel' ? 'active' : ''}`}
                                        onClick={() => onSelectTool && onSelectTool('WindowLevel')}
                                        title="Window/Level (Click and drag)"
                                        disabled={fileInfo?.isMP4}
                                    >
                                        🔆
                                    </button>
                                    <button
                                        className={`tool-button ${activeTool === 'Pan' ? 'active' : ''}`}
                                        onClick={() => onSelectTool && onSelectTool('Pan')}
                                        title="Pan"
                                        disabled={fileInfo?.isMP4}
                                    >
                                        ✋
                                    </button>
                                    <button
                                        className={`tool-button ${activeTool === 'Zoom' ? 'active' : ''}`}
                                        onClick={() => onSelectTool && onSelectTool('Zoom')}
                                        title="Zoom"
                                        disabled={fileInfo?.isMP4}
                                    >
                                        🔍
                                    </button>
                                    <button
                                        className="tool-button"
                                        onClick={onResetViewport}
                                        title="Reset"
                                    >
                                        🔄
                                    </button>
                                    <button
                                        className="tool-button"
                                        onClick={onInvertImage}
                                        title="Invert"
                                        disabled={fileInfo?.isMP4}
                                    >
                                        🔲
                                    </button>
                                </div>
                            </div>

                            {/* Grid Layout section */}
                            {onGridLayoutChange && (
                                <div className="tool-section">
                                    <Typography variant="subtitle2" gutterBottom>
                                        Grid Layout
                                    </Typography>
                                    <div className="grid-layout-container">
                                        {/* Grid layout options */}
                                        <div className="grid-layout-options">
                                            {[
                                                { rows: 1, cols: 1 },
                                                { rows: 1, cols: 2 },
                                                { rows: 2, cols: 1 },
                                                { rows: 2, cols: 2 },
                                                { rows: 1, cols: 3 },
                                                { rows: 3, cols: 1 },
                                                { rows: 2, cols: 3 },
                                                { rows: 3, cols: 2 },
                                                { rows: 3, cols: 3 },
                                                { rows: 2, cols: 4 },
                                                { rows: 4, cols: 2 },
                                                { rows: 4, cols: 4 }
                                            ].map((layout, index) => (
                                                <button
                                                    key={index}
                                                    className={`grid-layout-button ${currentGridLayout && currentGridLayout.rows === layout.rows && currentGridLayout.cols === layout.cols ? 'active' : ''}`}
                                                    onClick={() => onGridLayoutChange(layout)}
                                                    title={`${layout.rows}x${layout.cols} grid`}
                                                >
                                                    <div
                                                        className="grid-preview"
                                                        style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: `repeat(${layout.cols}, 1fr)`,
                                                            gridTemplateRows: `repeat(${layout.rows}, 1fr)`,
                                                            gap: '2px',
                                                            width: '24px',
                                                            height: '24px'
                                                        }}
                                                    >
                                                        {Array.from({ length: layout.rows * layout.cols }).map((_, i) => (
                                                            <div
                                                                key={i}
                                                                style={{
                                                                    background: currentGridLayout && currentGridLayout.rows === layout.rows && currentGridLayout.cols === layout.cols ? '#fff' : '#666',
                                                                    borderRadius: '2px'
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                    <span className="grid-label">{layout.rows}x{layout.cols}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Measure Tab */}
                    {activeTab === 1 && (
                        <div className="measure-panel-container">
                            {fileInfo?.isMP4 && isPlaying ? (
                                <Box p={3} textAlign="center">
                                    <Typography variant="h6" gutterBottom>
                                        Video is Playing
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" paragraph>
                                        Please pause the video to use measurement tools
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={onTogglePlayPause}
                                        startIcon={<span>⏸️</span>}
                                    >
                                        Pause Video
                                    </Button>
                                </Box>
                            ) : (
                                <EnhancedMeasurementPanel
                                    onSelectMeasurement={onMeasurementSelection}
                                    onMeasurementComplete={onMeasurementComplete}
                                    activeMeasurementTool={activeMeasurementTool || 'WindowLevel'}
                                    patientGender={patientInfo?.gender || 'male'}
                                    patientAge={patientInfo?.age}
                                    currentImageId={selectedFile?.imageId || selectedFile?.filename || fileName}
                                    isInMeasurementMode={isInMeasurementMode || false}
                                    onClearAllMeasurements={onClearAllMeasurements}
                                    onToolActivation={onToolActivation}
                                />
                            )}
                        </div>
                    )}

                    {/* Report Tab */}
                    {activeTab === 2 && (
                        <div className="report-panel-container">
                            <CompactPocusReport fileName={fileName || selectedFile?.filename || 'Unknown'} />
                        </div>
                    )}

                    {/* Notes Tab */}
                    {activeTab === 3 && (
                        <div className="notes-panel-container">
                            <h3>Study Notes</h3>
                            <textarea
                                className="notes-textarea"
                                value={studyNotes}
                                onChange={(e) => setStudyNotes(e.target.value)}
                                placeholder="Type your notes about this study here..."
                            />

                            {/* Display window/level if available */}
                            {windowWidth && windowCenter && (
                                <div className="window-level-info">
                                    <p>Window Width: {windowWidth}</p>
                                    <p>Window Center: {windowCenter}</p>
                                </div>
                            )}
                        </div>
                    )}
                        </div>
                    </>
                )}
            </Drawer>
        </>
    );
};

export default ViewerDrawer;
